import React, { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { Channel, OnlineUser, PresenceStatus, User, handleAvatarError } from '../../types';
import { resolveAvatarUrl } from '../../utils/serverUrl';
import { STATUS_LABEL } from '../../utils/presence';
import { PresenceDot } from '../PresenceDot';

/** 카드에 보여 줄 최대 인원 — 5열 × 2줄. 나머지는 '전체 보기'(온라인 탭) */
const MAX_SHOWN = 10;

/** 같은 그룹 안에서 '접속중'을 '자리 비움·다른 용무 중'보다 앞에 둔다 — 바로 답을 받을 가능성 순 */
const STATUS_RANK: Record<PresenceStatus, number> = { online: 0, away: 1, busy: 2, offline: 3 };

interface OnlineColleaguesCardProps {
  currentUser: User;
  channels: Channel[];
  onlineUsers: OnlineUser[];
  onUserClick?: (user: User) => void;
  onOpenOrg?: () => void;
}

/**
 * 홈의 '지금 대화할 수 있는 동료'(넓은 홈·한 줄 배치 공용). 고르는 순서:
 * ① 나와 1:1 대화방이 있는 상대(최근 대화순) → ② 같은 계열사·같은 부서 → ③ 같은 계열사 나머지.
 * 타 계열사 사람은 1:1 상대일 때만 들어온다(조직도 탭과 달리 여기는 '자주 볼 사람' 자리다).
 * 데이터는 App이 이미 들고 있는 onlineUsers·channels뿐 — 서버 요청 0.
 */
export const OnlineColleaguesCard: React.FC<OnlineColleaguesCardProps> = React.memo(({
  currentUser,
  channels,
  onlineUsers,
  onUserClick,
  onOpenOrg,
}) => {
  const myId = String(currentUser.id);

  const { shown, total } = useMemo(() => {
    const others = onlineUsers.filter(u => String(u.userId) !== myId);

    // 1:1 상대 → 최근 대화 순위. dmPartnerId(사번)만 쓴다 — 이름 매칭은 동명이인이 섞인다
    const dmRank = new Map<string, number>();
    channels
      .filter(ch => ch.type === 'dm' && ch.dmPartnerId && !ch.isArchived)
      .sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''))
      .forEach((ch, i) => {
        const id = String(ch.dmPartnerId);
        if (!dmRank.has(id)) dmRank.set(id, i);
      });

    const byStatusThenName = (a: OnlineUser, b: OnlineUser) =>
      STATUS_RANK[a.status] - STATUS_RANK[b.status] || a.userName.localeCompare(b.userName, 'ko');

    const dmPartners = others
      .filter(u => dmRank.has(String(u.userId)))
      .sort((a, b) =>
        STATUS_RANK[a.status] - STATUS_RANK[b.status]
        || (dmRank.get(String(a.userId)) ?? 0) - (dmRank.get(String(b.userId)) ?? 0));
    const rest = others.filter(u => !dmRank.has(String(u.userId)) && u.affiliateId === currentUser.affiliateId);
    const sameDept = rest.filter(u => !!currentUser.depart && u.depart === currentUser.depart).sort(byStatusThenName);
    const sameAffiliate = rest.filter(u => !currentUser.depart || u.depart !== currentUser.depart).sort(byStatusThenName);

    return {
      shown: [...dmPartners, ...sameDept, ...sameAffiliate].slice(0, MAX_SHOWN),
      total: others.length,
    };
  }, [onlineUsers, channels, myId, currentUser.affiliateId, currentUser.depart]);

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100 pl-[18px] pr-2.5 pt-3.5 pb-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-bold text-slate-600 truncate">
          지금 대화할 수 있는 동료
          {total > 0 && <span className="font-medium text-slate-500"> · 접속 중 {total}명</span>}
        </h2>
        {onOpenOrg && (
          <button
            type="button"
            onClick={onOpenOrg}
            className="h-8 pl-2.5 pr-1.5 shrink-0 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-800 flex items-center gap-0.5 transition-colors"
          >
            전체 보기
            <ChevronRight size={15} aria-hidden="true" />
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="pr-2">
          <p className="text-sm font-semibold text-slate-700">지금 접속한 동료가 없습니다</p>
          <p className="mt-1 text-[13px] text-slate-500">온라인 탭에서 전체 동료를 보고 바로 대화할 수 있습니다</p>
        </div>
      ) : (
        <ul className="grid grid-cols-5 gap-x-1.5 gap-y-3.5 pr-2">
          {shown.map(u => (
            <li key={u.userId} className="min-w-0">
              <button
                type="button"
                onClick={() => onUserClick?.({
                  id: u.userId,
                  username: u.userName,
                  avatar: u.avatar || '',
                  depart: u.depart || undefined,
                  affiliateId: u.affiliateId || undefined,
                  status: u.status,
                } as User)}
                aria-label={`${u.userName}${u.depart ? `, ${u.depart}` : ''}, ${STATUS_LABEL[u.status]}`}
                title={`${u.userName}${u.depart ? ` · ${u.depart}` : ''}`}
                className="w-full flex flex-col items-center gap-1 rounded-xl py-1 hover:bg-slate-50 transition-colors"
              >
                {/* 아바타는 최대 48px이고 칸이 더 좁으면 칸 폭에 맞춰 줄어든다 — 좁은 PC앱 최소 폭(380px)에서 5열이 한 칸 44px */}
                <span className="relative w-full max-w-[3rem]">
                  <img
                    src={resolveAvatarUrl(u.avatar)} onError={handleAvatarError}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="block w-full aspect-square rounded-full object-cover bg-slate-100"
                  />
                  <PresenceDot status={u.status} className="absolute bottom-0 right-0 w-3.5 h-3.5 border-2 border-white" />
                </span>
                <span className="max-w-full text-[13px] font-semibold text-slate-800 truncate">{u.userName}</span>
                <span className="max-w-full -mt-1 text-[11px] text-slate-500 truncate">{u.depart || ' '}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
});
OnlineColleaguesCard.displayName = 'OnlineColleaguesCard';
